import { useState, useEffect, useCallback } from "react";
import { Plus, TrendingUp, Calendar, Crown, Loader2, MapPin, Home, User, Users, Trophy, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { getIconByName } from "@/components/team/performanceMetricsConfig";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useNavigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { TeamPerformanceMetric, TeamMemberPerformance } from "@/types/team";
import type { D2DUnit, D2DArea } from "@/types/d2d";
import type { Customer } from "@/types/customer";

interface PerformanceWithDetails extends TeamMemberPerformance {
  metric?: TeamPerformanceMetric;
  unit?: {
    id: string;
    unit_name?: string;
    house?: {
      house_number: string;
      street?: {
        name: string;
        area?: {
          name: string;
        };
      };
    };
  };
}

interface UnitWithLocation extends D2DUnit {
  house?: {
    house_number: string;
    street?: {
      name: string;
      area_id: string;
      area?: {
        name: string;
      };
    };
  };
}

const Performance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<TeamPerformanceMetric[]>([]);
  const [performances, setPerformances] = useState<PerformanceWithDetails[]>([]);
  const [units, setUnits] = useState<UnitWithLocation[]>([]);
  const [areas, setAreas] = useState<D2DArea[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [expandedPerformances, setExpandedPerformances] = useState<Set<string>>(new Set());
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([]);
  const [assignmentTab, setAssignmentTab] = useState<"unit" | "customer">("unit");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Initialize sidebar state from localStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Load team ID and data
  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.team_id) {
        setLoading(false);
        return;
      }

      setTeamId(profile.team_id);

      // Load metrics, performances, units, areas, and customers in parallel
      const [metricsResult, performancesResult, unitsResult, areasResult, customersResult] = await Promise.all([
        supabase
          .from('team_performance_metrics')
          .select('*')
          .eq('team_id', profile.team_id)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('team_member_performance')
          .select('*')
          .eq('user_id', user.id)
          .order('recorded_at', { ascending: false })
          .limit(50),
        supabase
          .from('d2d_units')
          .select(`
            id,
            unit_name,
            tags,
            house_id,
            house:d2d_houses!inner(
              house_number,
              street:d2d_streets!inner(
                name,
                area_id,
                area:d2d_areas!inner(
                  name
                )
              )
            )
          `)
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(200),
        supabase
          .from('d2d_areas')
          .select('*')
          .eq('created_by', user.id)
          .order('name'),
        supabase
          .from('customers')
          .select('*')
          .eq('user_id', user.id)
          .order('first_name')
      ]);

      if (metricsResult.data) {
        setMetrics(metricsResult.data);
      }

      if (performancesResult.data) {
        // Enrich performances with metric details and unit info
        const enrichedPerformances = performancesResult.data.map(perf => {
          const unit = unitsResult.data?.find(u => u.id === perf.unit_id);
          return {
            ...perf,
            metric: metricsResult.data?.find(m => m.id === perf.metric_id),
            unit: unit ? {
              id: unit.id,
              unit_name: unit.unit_name,
              house: (unit as UnitWithLocation).house
            } : undefined
          };
        });
        setPerformances(enrichedPerformances);
      }

      if (unitsResult.data) {
        setUnits(unitsResult.data as UnitWithLocation[]);
      }

      if (areasResult.data) {
        setAreas(areasResult.data);
        // Set active area as default if exists
        const activeArea = areasResult.data.find(a => a.is_active);
        if (activeArea) {
          setSelectedAreaId(activeArea.id);
        }
      }

      if (customersResult.data) {
        setCustomers(customersResult.data as Customer[]);
      }

    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle metric selection
  const toggleMetric = (metricId: string) => {
    setSelectedMetricIds(prev => 
      prev.includes(metricId) 
        ? prev.filter(id => id !== metricId)
        : [...prev, metricId]
    );
  };

  // Handle adding performance records
  const handleAddPerformance = async () => {
    if (!user || selectedMetricIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Leistung aus.');
      return;
    }

    // Require either unit or customer selection
    if (assignmentTab === "unit" && !selectedUnitId) {
      toast.error('Bitte wählen Sie eine Einheit aus.');
      return;
    }
    if (assignmentTab === "customer" && !selectedCustomerId) {
      toast.error('Bitte wählen Sie einen Kunden aus.');
      return;
    }

    setSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Create performance records for each selected metric
      const records = selectedMetricIds.map(metricId => {
        const metric = metrics.find(m => m.id === metricId);
        return {
          metric_id: metricId,
          user_id: user.id,
          points: metric?.points_value || 1,
          notes: notes.trim() || null,
          unit_id: assignmentTab === "unit" ? selectedUnitId : null,
          recorded_at: today,
          recorded_by: user.id
        };
      });

      const { error: insertError } = await supabase
        .from('team_member_performance')
        .insert(records);

      if (insertError) throw insertError;

      // Tag the unit with WIN if unit was selected
      if (assignmentTab === "unit" && selectedUnitId) {
        const { data: unitData } = await supabase
          .from('d2d_units')
          .select('tags')
          .eq('id', selectedUnitId)
          .single();

        const currentTags = unitData?.tags || [];
        if (!currentTags.includes('WIN')) {
          await supabase
            .from('d2d_units')
            .update({ 
              tags: [...currentTags, 'WIN'],
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedUnitId);
        }
      }

      // If customer was selected, add note to customer
      if (assignmentTab === "customer" && selectedCustomerId) {
        const customer = customers.find(c => c.id === selectedCustomerId);
        const metricsNames = selectedMetricIds.map(id => metrics.find(m => m.id === id)?.name).filter(Boolean).join(', ');
        const performanceNote = `[${new Date().toLocaleDateString('de-DE')} ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}] Leistung erfasst: ${metricsNames}${notes ? ` - ${notes}` : ''}`;
        
        await supabase
          .from('customers')
          .update({ 
            notes: customer?.notes ? `${customer.notes}\n${performanceNote}` : performanceNote,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedCustomerId);
      }

      toast.success(`${selectedMetricIds.length} Leistung(en) erfasst`);
      setIsDialogOpen(false);
      setSelectedMetricIds([]);
      setSelectedUnitId("");
      setSelectedCustomerId("");
      setNotes("");
      
      // Reload data
      loadData();

    } catch (error) {
      console.error('Error recording performance:', error);
      toast.error('Fehler beim Erfassen der Leistung');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter units by selected area
  const filteredUnits = selectedAreaId 
    ? units.filter(unit => unit.house?.street?.area_id === selectedAreaId)
    : units;

  // Calculate current month stats
  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const currentMonth = new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
  
  const currentMonthPerformances = performances.filter(p => 
    p.recorded_at.startsWith(currentMonthKey)
  );

  const metricStats = metrics.map(metric => {
    const count = currentMonthPerformances.filter(p => p.metric_id === metric.id).length;
    const points = currentMonthPerformances
      .filter(p => p.metric_id === metric.id)
      .reduce((sum, p) => sum + p.points, 0);
    return { metric, count, points };
  });

  const totalPoints = currentMonthPerformances.reduce((sum, p) => sum + p.points, 0);

  // Get unit display info with location
  const getUnitLabel = (unit: UnitWithLocation) => {
    const baseName = unit.unit_name || `Einheit`;
    const housePart = unit.house?.house_number ? ` Nr. ${unit.house.house_number}` : '';
    const streetPart = unit.house?.street?.name ? `, ${unit.house.street.name}` : '';
    return `${baseName}${housePart}${streetPart}`;
  };

  // Get customer display info
  const getCustomerLabel = (customer: Customer) => {
    return `${customer.first_name}${customer.last_name ? ` ${customer.last_name}` : ''}`;
  };

  // Toggle expanded state for a performance
  const toggleExpanded = (perfId: string) => {
    setExpandedPerformances(prev => {
      const newSet = new Set(prev);
      if (newSet.has(perfId)) {
        newSet.delete(perfId);
      } else {
        newSet.add(perfId);
      }
      return newSet;
    });
  };

  // Navigate to unit in running list
  const navigateToUnit = (perf: PerformanceWithDetails) => {
    if (perf.unit?.house?.street?.area) {
      // Navigate to running list with area context
      navigate('/running-list');
    }
  };

  // Get source info for a performance
  const getSourceInfo = (perf: PerformanceWithDetails) => {
    if (perf.unit) {
      const streetName = perf.unit.house?.street?.name || '';
      const houseNumber = perf.unit.house?.house_number || '';
      const areaName = perf.unit.house?.street?.area?.name || '';
      const unitName = perf.unit.unit_name || 'Einheit';
      
      return {
        type: 'unit' as const,
        label: `${streetName} ${houseNumber}${unitName ? ` - ${unitName}` : ''}`,
        sublabel: areaName ? `Gebiet: ${areaName}` : 'Laufliste',
        icon: Home
      };
    }
    
    // Check notes for customer info
    if (perf.notes?.includes('Kunde:')) {
      return {
        type: 'customer' as const,
        label: perf.notes,
        sublabel: 'Kundenverwaltung',
        icon: User
      };
    }
    
    return null;
  };

  if (loading) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!teamId) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 px-4">
                <h1 className="text-lg font-bold">Leistungserfassung</h1>
              </div>
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-md">
                <CardContent className="text-center py-8">
                  <Trophy className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Kein Team zugewiesen</h3>
                  <p className="text-muted-foreground">
                    Sie müssen einem Team beitreten, um Leistungen zu erfassen.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (metrics.length === 0) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 px-4">
                <h1 className="text-lg font-bold">Leistungserfassung</h1>
              </div>
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-md">
                <CardContent className="text-center py-8">
                  <Trophy className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Metriken definiert</h3>
                  <p className="text-muted-foreground">
                    Ihr Teamleiter hat noch keine Leistungsmetriken für Ihr Team definiert.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-between px-4">
              <h1 className="text-lg font-bold">Leistungserfassung</h1>
              <div className="flex items-center gap-2">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" size="sm">
                      <Plus className="h-4 w-4" />
                      <span className="hidden sm:inline">Leistung erfassen</span>
                      <span className="sm:hidden">Neu</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Leistung erfassen</DialogTitle>
                      <DialogDescription>
                        Wählen Sie die Leistungen und die zugehörige Einheit aus.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Metric Selection */}
                      <div className="space-y-2">
                        <Label>Leistungen (mehrere auswählbar)</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {metrics.map(metric => (
                            <Button
                              key={metric.id}
                              type="button"
                              variant={selectedMetricIds.includes(metric.id) ? "default" : "outline"}
                              className="justify-start h-auto py-2 px-3"
                              onClick={() => toggleMetric(metric.id)}
                            >
                              <div className="text-left">
                                <div className="font-medium text-sm">{metric.name}</div>
                                <div className="text-xs opacity-70">{metric.points_value} Punkte</div>
                              </div>
                            </Button>
                          ))}
                        </div>
                        {selectedMetricIds.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {selectedMetricIds.length} Leistung(en) ausgewählt
                          </p>
                        )}
                      </div>

                      {/* Assignment Selection with Tabs */}
                      <Tabs value={assignmentTab} onValueChange={(v) => {
                        setAssignmentTab(v as "unit" | "customer");
                        // Reset selections when switching tabs
                        if (v === "customer") {
                          setSelectedUnitId("");
                          setSelectedAreaId("");
                        } else {
                          setSelectedCustomerId("");
                          // Reset to active area
                          const activeArea = areas.find(a => a.is_active);
                          if (activeArea) setSelectedAreaId(activeArea.id);
                        }
                      }}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="unit" className="gap-2">
                            <MapPin className="h-3 w-3" />
                            Gebiet / Einheit
                          </TabsTrigger>
                          <TabsTrigger value="customer" className="gap-2">
                            <User className="h-3 w-3" />
                            Kunde
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="unit" className="space-y-3 mt-3">
                          {/* Area Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Gebiet auswählen</Label>
                            <Select value={selectedAreaId} onValueChange={(v) => {
                              setSelectedAreaId(v);
                              setSelectedUnitId(""); // Reset unit when area changes
                            }}>
                              <SelectTrigger>
                                <SelectValue placeholder="Gebiet auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {areas.map(area => (
                                  <SelectItem key={area.id} value={area.id}>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-3 w-3" />
                                      {area.name}
                                      {area.plz && <span className="text-xs text-muted-foreground">({area.plz})</span>}
                                      {area.is_active && (
                                        <Badge variant="default" className="text-xs ml-1">Aktiv</Badge>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Unit Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Einheit zuordnen</Label>
                            <Select 
                              value={selectedUnitId} 
                              onValueChange={setSelectedUnitId}
                              disabled={!selectedAreaId}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={selectedAreaId ? "Einheit auswählen" : "Zuerst Gebiet wählen"} />
                              </SelectTrigger>
                              <SelectContent>
                                {filteredUnits.map(unit => (
                                  <SelectItem key={unit.id} value={unit.id}>
                                    <div className="flex items-center gap-2">
                                      <Home className="h-3 w-3" />
                                      {getUnitLabel(unit)}
                                      {unit.tags?.includes('WIN') && (
                                        <Badge variant="secondary" className="text-xs ml-1">WIN</Badge>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                                {filteredUnits.length === 0 && selectedAreaId && (
                                  <div className="p-2 text-xs text-muted-foreground text-center">
                                    Keine Einheiten in diesem Gebiet
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </TabsContent>

                        <TabsContent value="customer" className="space-y-3 mt-3">
                          {/* Customer Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Kunde auswählen</Label>
                            <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Kunde auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {customers.map(customer => (
                                  <SelectItem key={customer.id} value={customer.id}>
                                    <div className="flex items-center gap-2">
                                      <User className="h-3 w-3" />
                                      {getCustomerLabel(customer)}
                                      {customer.email && (
                                        <span className="text-xs text-muted-foreground">({customer.email})</span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                                {customers.length === 0 && (
                                  <div className="p-2 text-xs text-muted-foreground text-center">
                                    Keine Kunden vorhanden
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </TabsContent>
                      </Tabs>

                      {/* Notes */}
                      <div className="space-y-2">
                        <Label className="text-xs">Kommentar (optional)</Label>
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Zusätzliche Informationen..."
                          rows={2}
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                          Abbrechen
                        </Button>
                        <Button 
                          onClick={handleAddPerformance}
                          disabled={submitting || selectedMetricIds.length === 0 || (assignmentTab === "unit" ? !selectedUnitId : !selectedCustomerId)}
                        >
                          {submitting ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          Erfassen
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-4 md:py-8">
              <div className="max-w-4xl mx-auto">
                
                {/* Current Month Stats */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-5 w-5" />
                    <h2 className="text-lg font-semibold">{currentMonth}</h2>
                  </div>

                  {/* Total Points - moved to top */}
                  <Card className="mb-4">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gradient-to-r from-primary to-primary/80">
                          <TrendingUp className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Gesamtpunkte {currentMonth}</p>
                          <p className="text-2xl font-bold">{totalPoints}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Metric Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {metricStats.map(({ metric, count, points }) => {
                      const MetricIcon = getIconByName(metric.icon || 'trophy');
                      return (
                      <Card key={metric.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <MetricIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">{metric.name}</p>
                              <p className="text-xl font-bold">{count}</p>
                              <p className="text-xs text-muted-foreground">{points} Punkte</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                    })}
                  </div>
                </div>

                {/* Recent Performances */}
                {currentMonthPerformances.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-base font-semibold mb-3">Aktuelle Leistungen</h3>
                    <div className="space-y-2">
                      {currentMonthPerformances.slice(0, 10).map(perf => {
                        const PerfIcon = getIconByName(perf.metric?.icon || 'trophy');
                        const isExpanded = expandedPerformances.has(perf.id);
                        const sourceInfo = getSourceInfo(perf);
                        
                        return (
                        <Card key={perf.id} className="overflow-hidden">
                          <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(perf.id)}>
                            <CollapsibleTrigger asChild>
                              <CardContent className="p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-3">
                                    <div className="p-1.5 rounded bg-primary/10">
                                      <PerfIcon className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium">{perf.metric?.name || 'Unbekannte Metrik'}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(perf.recorded_at).toLocaleDateString('de-DE')}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary">{perf.points} Pkt.</Badge>
                                    {sourceInfo ? (
                                      isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      )
                                    ) : null}
                                  </div>
                                </div>
                              </CardContent>
                            </CollapsibleTrigger>
                            
                            {sourceInfo && (
                              <CollapsibleContent>
                                <div className="px-3 pb-3 pt-0">
                                  <div className="border-t pt-3">
                                    <p className="text-xs font-medium text-muted-foreground mb-2">Quelle</p>
                                    <Button
                                      variant="ghost"
                                      className="w-full justify-start h-auto py-2 px-3 bg-muted/50 hover:bg-muted"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (sourceInfo.type === 'unit') {
                                          navigate('/running-list');
                                        } else if (sourceInfo.type === 'customer') {
                                          navigate('/customer-management');
                                        }
                                      }}
                                    >
                                      <div className="flex items-center gap-3 w-full">
                                        <div className="p-1.5 rounded bg-background">
                                          <sourceInfo.icon className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="flex-1 text-left">
                                          <p className="text-sm font-medium">{sourceInfo.label}</p>
                                          <p className="text-xs text-muted-foreground">{sourceInfo.sublabel}</p>
                                        </div>
                                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    </Button>
                                  </div>
                                  {perf.notes && (
                                    <p className="text-xs text-muted-foreground mt-2">{perf.notes}</p>
                                  )}
                                </div>
                              </CollapsibleContent>
                            )}
                          </Collapsible>
                        </Card>
                      );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {performances.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-12">
                      <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Keine Leistungen erfasst</h3>
                      <p className="text-muted-foreground mb-4">
                        Erfassen Sie Ihre erste Leistung, um mit dem Tracking zu beginnen.
                      </p>
                      <Button className="gap-2" onClick={() => setIsDialogOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Erste Leistung erfassen
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Performance;
